if ( !customElements.get( 'scroll-progress-bar' ) )
{
	class ScrollProgressBar extends HTMLElement {
		constructor() {
			super();
		}

		get totalWidth() {
			return this.contentContainer.offsetWidth;
		}

		get targetId() {
			return this.getAttribute('target');
		}

		get target() {
			return document.getElementById(this.targetId);
		}

		get targetScrollLeft() {
			return this.target.scrollLeft;
		}

		set targetScrollLeft(value) {
			this.target.scrollLeft = value;
		}

		connectedCallback() {
			// Create media query list
			this.mediaQuery = window.matchMedia(FoxTheme.config.mediaQueryMobile);

			// Create ResizeObserver
			this.resizeObserver = new ResizeObserver(() => {
				if (this.mediaQuery.matches) {
					this.init();
				}
			});

			// Store media query handler
			this.mediaQueryHandler = (event) => {
				if (event.matches) {
					this.init();
				} else {
					this.disable();
				}
			};

			// Add media query change listener
			this.mediaQuery.addEventListener('change', this.mediaQueryHandler);

			// Initial check
			if (!this.mediaQuery.matches) {
				this.disable();
				return;
			}

			this.init();
		}

		init() {
			if (!this.target) {
				console.error('Target element not found');
				return;
			}

			this.contentContainer = this.target.children[0];
			this.progressBar = this.querySelector('.progress-bar');

			const targetStyle = window.getComputedStyle(this.target);
			this.targetPadding = parseFloat(targetStyle.paddingLeft) + parseFloat(targetStyle.paddingRight);

			// Start observing target for size changes
			this.resizeObserver.observe(this.target);

			// Check if content overflows
			const viewportWidth = this.target.clientWidth - this.targetPadding;

			if (this.totalWidth <= viewportWidth) {
				this.disable();
				return;
			}

			// Calculate initial progress
			this.calculateInitialProgress();

			// Add scroll listener
			this.scrollHandler = () => {
				requestAnimationFrame(() => this.updateProgress());
			};
			this.target.addEventListener('scroll', this.scrollHandler);

			// Show progress bar and do initial update
			this.style.display = 'block';
			this.updateProgress();
		}

		disable() {
			// Remove scroll listener if exists
			if (this.scrollHandler && this.target) {
				this.target.removeEventListener('scroll', this.scrollHandler);
			}

			// Hide progress bar
			this.style.display = 'none';

			// Reset progress
			if (this.progressBar) {
				this.progressBar.style.width = '0%';
			}

			// Stop observing target
			if (this.target) {
				this.resizeObserver.unobserve(this.target);
			}
		}

		disconnectedCallback() {
			// Clean up media query listener with stored handler
			this.mediaQuery.removeEventListener('change', this.mediaQueryHandler);
			// Disconnect resize observer
			this.resizeObserver.disconnect();
			this.disable();
		}

		calculateInitialProgress() {
			const viewportWidth = this.target.clientWidth - this.targetPadding;
			this.initialProgress = (viewportWidth / this.totalWidth) * 100;

			this.target.addEventListener('scroll', () => {
				requestAnimationFrame(() => this.updateProgress());
			});
			this.updateProgress();
		}

		updateProgress() {
			const factor = FoxTheme.config.isRTL ? -1 : 1;
			const viewportWidth = this.target.clientWidth - this.targetPadding;
			const scrolled = factor * this.targetScrollLeft;
			const maxScroll = this.totalWidth - viewportWidth;

			const scrollProgress = (scrolled / maxScroll) * (100 - this.initialProgress);
			const totalProgress = this.initialProgress + scrollProgress;

			const clampedProgress = Math.min(100, Math.max(this.initialProgress, totalProgress));

			this.progressBar.style.width = `${clampedProgress}%`;
		}
	}

	customElements.define('scroll-progress-bar', ScrollProgressBar);
}